import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ProjectCards } from '@/components/projects/ProjectCards';
import MaintenanceFilters from './MaintenanceFilters';
import MaintenanceStatsPanel from './MaintenanceStatsPanel';
import MaintenanceProjectTable from './MaintenanceProjectTable';
import ViewModeToggle, { type ViewMode } from './ViewModeToggle';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMaintenanceProjectsQuery } from '@/hooks/maintenance/useMaintenanceProjectsQuery';
import type { MaintenanceFiltersData } from '@/hooks/maintenance/useMaintenanceFiltersState';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';

interface MaintenanceProjectListProps {
  filters: MaintenanceFiltersData;
  onFiltersChange: (filters: MaintenanceFiltersData) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export default function MaintenanceProjectList({ 
  filters, 
  onFiltersChange,
  currentPage,
  onPageChange 
}: MaintenanceProjectListProps) {
  const pageSize = 50;
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const v = localStorage.getItem('maintenance-view-mode-v1');
      return v === 'card' || v === 'table' ? v : 'table';
    } catch { return 'table'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('maintenance-view-mode-v1', viewMode); } catch {}
  }, [viewMode]);
  const effectiveView: ViewMode = isMobile ? 'card' : viewMode;

  const handleStageToggle = (stage: import('@/constants/lifecycleStages').LifecycleStage) => {
    onFiltersChange({
      ...filters,
      lifecycleStage: filters.lifecycleStage === stage ? '' : stage,
      watchSubstatus: '',
    });
  };

  const handleWatchSubstatusToggle = (
    sub: import('@/constants/lifecycleStages').LifecycleWatchSubstatus,
    stage: import('@/constants/lifecycleStages').LifecycleStage
  ) => {
    onFiltersChange({
      ...filters,
      lifecycleStage: stage,
      watchSubstatus:
        filters.watchSubstatus === sub && filters.lifecycleStage === stage ? '' : sub,
    });
  };

  const {
    data,
    isLoading,
    isError,
    error,
    refetch
  } = useMaintenanceProjectsQuery(filters, currentPage, pageSize);

  const projects = data?.projects || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Helper for active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.projectType.length > 0) count++;
    if (filters.company) count++;
    if (filters.projectMark) count++;
    if (filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0) count++;
    if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) count++;
    if (filters.lifecycleStage) count++;
    if (filters.handoverDateFrom || filters.handoverDateTo) count++;
    return count;
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">
            <p>Không thể tải danh sách dự án bảo trì</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error?.message || 'Đã xảy ra lỗi không xác định'}
            </p>
            <Button onClick={handleRefresh} variant="outline" className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Thử lại
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <MaintenanceStatsPanel
        filters={filters}
        activeStage={filters.lifecycleStage}
        onStageToggle={handleStageToggle}
        activeWatchSubstatus={(filters.watchSubstatus as import('@/constants/lifecycleStages').LifecycleWatchSubstatus) || null}
        onWatchSubstatusToggle={handleWatchSubstatusToggle}
      />

      {/* Filters */}
      <MaintenanceFilters
        filters={filters}
        onFiltersChange={onFiltersChange}
        isLoading={isLoading}
        rightSlot={!isMobile ? <ViewModeToggle value={viewMode} onChange={setViewMode} /> : undefined}
      />

      {/* Pagination Info */}
      {totalCount > 0 && (
        <div className="text-sm text-muted-foreground">
          Hiển thị {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalCount)} của {totalCount} dự án
        </div>
      )}

      {/* Empty State */}
      {!isLoading && projects.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-muted-foreground">
              {getActiveFiltersCount() > 0 ? (
                <>
                  <p>Không tìm thấy dự án bảo trì nào phù hợp với bộ lọc</p>
                  <p className="text-sm mt-1">Thử thay đổi điều kiện lọc để xem kết quả khác</p>
                </>
              ) : (
                <>
                  <p>Chưa có dự án bảo trì nào</p>
                  <p className="text-sm mt-1">Các dự án với trạng thái "Bảo hành" sẽ hiển thị ở đây</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Projects */}
      {projects.length > 0 && (
        effectiveView === 'table'
          ? <MaintenanceProjectTable projects={projects} isLoading={isLoading} />
          : <ProjectCards projects={projects} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(Math.max(1, currentPage - 1));
                  }}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onPageChange(page);
                        }}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return (
                    <PaginationItem key={page}>
                      <span className="px-3 py-2">...</span>
                    </PaginationItem>
                  );
                }
                return null;
              })}
              
              <PaginationItem>
                <PaginationNext 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(Math.min(totalPages, currentPage + 1));
                  }}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}